import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  AmountCurrencyRequest,
  SavedPaymentAccountFields,
  StartDepositTransaction,
} from "@/api/cashier";
import { EnumActionType } from "@/api/cashier";
import { startTransactionMutation } from "@/api/cashier/@tanstack/react-query.gen";
import { useBankTransferImportantNotesModal } from "@/components/bank-transfer/bank-transfer-important-notes/useBankTransferImportantNotesModal";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useAmountForm } from "@/hooks/deposit/useAmountForm";
import { useMethod } from "@/hooks/deposit/useMethod";
import { ERoutes } from "@/utils/enums";
import { toMinorUnits } from "@/utils/helpers";

export const usePspFlowHandler = () => {
  const navigate = useNavigate();
  const { selectedAccount } = useAccount();
  const { selectedPaymentMethod, selectedSavedPaymentAccount } = useMethod();
  const { getAmountFormData } = useAmountForm();

  const [error, setError] = useState<string | null>(null);

  const { openModal: openImportantNotesModal, modal: importantNotesModal } =
    useBankTransferImportantNotesModal();

  const { mutateAsync: startTransaction, isPending } = useMutation({
    ...startTransactionMutation(),
  });

  // BUILD REQUEST PAYLOAD
  const buildPayload = useCallback(
    (
      formFields?: SavedPaymentAccountFields,
      additionalData?: Record<string, unknown>
    ): StartDepositTransaction => {
      if (!selectedAccount || !selectedPaymentMethod) {
        throw new Error("Missing account or payment method");
      }

      // Read the latest amount from the store, not a stale render closure, so the
      // sent amount matches what the submit guard validated.
      const amountFormData = getAmountFormData();

      const resolvedFormFields =
        formFields ?? selectedSavedPaymentAccount?.saved_payment_account_fields;

      return {
        payment_method_id: selectedPaymentMethod.id,
        trading_account_id: selectedAccount.id,
        trading_amount_with_currency: {
          amount: String(
            toMinorUnits(
              amountFormData.tradingValue,
              selectedAccount.currency.decimals
            )
          ),
          code: selectedAccount.currency.code,
        } as AmountCurrencyRequest,
        saved_payment_account_id: selectedSavedPaymentAccount?.id,
        validate_spa_fields: true,
        ...(resolvedFormFields ? { form_fields: resolvedFormFields } : {}),
        ...(additionalData ? { additional_data: additionalData } : {}),
      };
    },
    [
      getAmountFormData,
      selectedAccount,
      selectedPaymentMethod,
      selectedSavedPaymentAccount,
    ]
  );

  // FLOW HANDLERS
  const handleRedirectFlow = useCallback((url: string) => {
    let parsedUrl = url;
    try {
      parsedUrl = decodeURIComponent(url);
    } catch {
      // Ignore malformed percent-encoding; use the original URL.
    }
    window.location.href = parsedUrl;
  }, []);

  const handleIframeFlow = useCallback(
    (
      url: string,
      action_type: EnumActionType,
      reference_id: string,
      paymentMethodName: string,
      paymentMethodId: string
    ) => {
      navigate(`../${selectedAccount?.accountId}/${ERoutes.LoadingPage}`, {
        state: {
          action_type,
          iframe_url: url,
          confirm_url: `../${ERoutes.Completion}/${reference_id}`,
          reference_id,
          payment_method_name: paymentMethodName,
          payment_method_id: paymentMethodId,
        },
      });
    },
    [navigate, selectedAccount?.accountId]
  );

  const executeTransaction = useCallback(
    async (
      formFields?: SavedPaymentAccountFields,
      additionalData?: Record<string, unknown>
    ) => {
      try {
        setError(null);
        const payload = buildPayload(formFields, additionalData);
        const response = await startTransaction({ body: payload });

        const { action_type, redirect_url, reference_id } = response;

        const methodName = selectedPaymentMethod?.display_name ?? "";

        switch (action_type) {
          case EnumActionType.EnumActionTypeRedirect:
            handleRedirectFlow(redirect_url);
            break;

          case EnumActionType.EnumActionTypeIframe:
          case EnumActionType.EnumActionTypeIframeWithManualConfirm:
            handleIframeFlow(
              redirect_url,
              action_type,
              reference_id,
              methodName,
              selectedPaymentMethod?.id ?? ""
            );
            break;

          default:
            throw new Error(`Unsupported action type: ${action_type}`);
        }
      } catch (error) {
        const apiError = error as { key?: string; message?: string };
        setError(apiError.key ?? apiError.message ?? "Unknown error");
      }
    },
    [
      buildPayload,
      startTransaction,
      handleRedirectFlow,
      handleIframeFlow,
      selectedPaymentMethod,
    ]
  );

  // MAIN FLOW
  const handleFlow = useCallback(
    async (
      formFields?: SavedPaymentAccountFields,
      additionalData?: Record<string, unknown>
    ) => {
      if (!selectedPaymentMethod) {
        throw new Error("Missing payment method");
      }

      if (
        selectedPaymentMethod.action_type ===
        EnumActionType.EnumActionTypeBankTransfer
      ) {
        // Show important notes first; no transaction is started for bank transfer
        openImportantNotesModal(() =>
          navigate(`../${selectedAccount?.accountId}/${ERoutes.BankTransfer}`)
        );
        return;
      }

      // Non-bank-transfer flows execute immediately
      await executeTransaction(formFields, additionalData);
    },
    [
      executeTransaction,
      navigate,
      openImportantNotesModal,
      selectedAccount,
      selectedPaymentMethod,
    ]
  );

  const resetError = useCallback(() => setError(null), []);

  return {
    handleFlow,
    executeTransaction,
    error,
    resetError,
    isPending,
    importantNotesModal,
  };
};
